import { create } from "zustand";
import api from "../api/client.js";

// ─── AUTH STORE ───────────────────────────────────────────────────────────────
export const useAuthStore = create((set, get) => ({
  user:    null,
  token:   localStorage.getItem("vylapp_token") || null,
  loading: false,
  error:   null,

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("vylapp_token", data.token);
      api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      set({ user: data.user, token: data.token, loading: false });
      return { ok: true };
    } catch (err) {
      set({ error: err.response?.data?.error || "Login failed", loading: false });
      return { ok: false, error: err.response?.data?.error };
    }
  },

  register: async (username, email, password, displayName) => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.post("/auth/register", { username, email, password, displayName });
      localStorage.setItem("vylapp_token", data.token);
      api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      set({ user: data.user, token: data.token, loading: false });
      return { ok: true };
    } catch (err) {
      set({ error: err.response?.data?.error || "Registration failed", loading: false });
      return { ok: false, error: err.response?.data?.error };
    }
  },

  logout: async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("vylapp_token");
    delete api.defaults.headers.common["Authorization"];
    set({ user: null, token: null });
  },

  fetchMe: async () => {
    const token = get().token;
    if (!token) return;
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    try {
      const { data } = await api.get("/auth/me");
      set({ user: data });
    } catch { get().logout(); }
  },

  updateUser: (updates) => set(state => ({ user: { ...state.user, ...updates } })),
}));

// ─── FEED STORE ───────────────────────────────────────────────────────────────
export const useFeedStore = create((set, get) => ({
  vibes:    [],
  loading:  false,
  hasMore:  true,
  page:     1,
  tab:      "for-you",

  fetchVibes: async (reset = false) => {
    if (get().loading) return;
    set({ loading: true });
    const page = reset ? 1 : get().page;
    try {
      const { data } = await api.get(`/vibes?page=${page}&limit=20&tab=${get().tab}`);
      set(state => ({
        vibes:   reset ? data.vibes : [...state.vibes, ...data.vibes],
        page:    page + 1,
        hasMore: page < data.pages,
        loading: false,
      }));
    } catch {
      set({ loading: false });
    }
  },

  postVibe: async (payload) => {
    const { data } = await api.post("/vibes", payload);
    set(state => ({ vibes: [data, ...state.vibes] }));
    return data;
  },

  likeVibe: async (vibeId) => {
    const { data } = await api.post(`/vibes/${vibeId}/like`);
    set(state => ({
      vibes: state.vibes.map(v =>
        v._id === vibeId ? { ...v, likes: data.liked
          ? [...v.likes, "me"]
          : v.likes.filter(id => id !== "me"), likesCount: data.likesCount } : v
      ),
    }));
    return data;
  },

  repostVibe: async (vibeId) => {
    const { data } = await api.post(`/vibes/${vibeId}/repost`);
    return data;
  },

  addVibeFromSocket: (vibe) => set(state => ({ vibes: [vibe, ...state.vibes] })),

  setTab: (tab) => { set({ tab, vibes: [], page: 1, hasMore: true }); },
}));

// ─── CHAT STORE ───────────────────────────────────────────────────────────────
export const useChatStore = create((set, get) => ({
  chats:      [],
  activeChat: null,
  messages:   [],
  loading:    false,
  typing:     {},

  fetchChats: async () => {
    set({ loading: true });
    try {
      const { data } = await api.get("/chats");
      set({ chats: data, loading: false });
    } catch { set({ loading: false }); }
  },

  openChat: async (chat) => {
    set({ activeChat: chat, messages: [] });
    try {
      const { data } = await api.get(`/chats/${chat._id}/messages`);
      set({ messages: data });
    } catch {}
  },

  sendMessage: async (content) => {
    const { activeChat } = get();
    if (!activeChat) return;
    const { data } = await api.post(`/chats/${activeChat._id}/messages`, { content });
    set(state => ({ messages: [...state.messages, data] }));
    return data;
  },

  addMessageFromSocket: (msg) => {
    const { activeChat } = get();
    if (activeChat && msg.chatId === activeChat._id) {
      set(state => ({ messages: [...state.messages, msg.message] }));
    }
    // Update chat preview
    set(state => ({
      chats: state.chats.map(c =>
        c._id === msg.chatId ? { ...c, lastMessage: { content: msg.message.content, timestamp: new Date() } } : c
      ),
    }));
  },

  setTyping: ({ chatId, userId, user, isTyping }) => {
    set(state => ({
      typing: { ...state.typing, [`${chatId}:${userId}`]: isTyping ? user : null },
    }));
  },

  startDM: async (recipientId) => {
    const { data } = await api.post("/chats/dm", { recipientId });
    set(state => {
      const exists = state.chats.find(c => c._id === data._id);
      return { chats: exists ? state.chats : [data, ...state.chats] };
    });
    return data;
  },
}));

// ─── SPACES STORE ─────────────────────────────────────────────────────────────
export const useSpacesStore = create((set, get) => ({
  spaces:  [],
  loading: false,

  fetchSpaces: async () => {
    set({ loading: true });
    try {
      const [live, scheduled] = await Promise.all([
        api.get("/spaces?status=live"),
        api.get("/spaces?status=scheduled"),
      ]);
      set({ spaces: [...live.data, ...scheduled.data], loading: false });
    } catch { set({ loading: false }); }
  },

  createSpace: async (payload) => {
    const { data } = await api.post("/spaces", payload);
    set(state => ({ spaces: [data, ...state.spaces] }));
    return data;
  },

  goLive: async (spaceId) => {
    await api.post(`/spaces/${spaceId}/live`);
    set(state => ({
      spaces: state.spaces.map(s => s._id === spaceId ? { ...s, status: "live" } : s),
    }));
  },

  updateListeners: ({ spaceId, count }) => {
    set(state => ({
      spaces: state.spaces.map(s => s._id === spaceId ? { ...s, listenerCount: count } : s),
    }));
  },
}));

// ─── UI STORE ─────────────────────────────────────────────────────────────────
export const useUIStore = create((set) => ({
  tab:          "feed",
  sidebarOpen:  false,
  activeCall:   null,
  showStream:   false,
  showCompose:  false,
  incomingCall: null,
  notifications:[],
  unreadChats:  10,
  unreadAlerts: 5,
  toast:        null,

  setTab:          (tab)          => set({ tab }),
  toggleSidebar:   ()             => set(state => ({ sidebarOpen: !state.sidebarOpen })),
  setActiveCall:   (call)         => set({ activeCall: call }),
  setShowStream:   (v)            => set({ showStream: v }),
  setShowCompose:  (v)            => set({ showCompose: v }),
  setIncomingCall: (call)         => set({ incomingCall: call }),
  clearIncoming:   ()             => set({ incomingCall: null }),
  showToast: (message, type="success") => {
    set({ toast: { message, type } });
    setTimeout(() => set({ toast: null }), 3000);
  },
  addNotification: (notif) => set(state => ({
    notifications: [notif, ...state.notifications],
    unreadAlerts: state.unreadAlerts + 1,
  })),
  markAlertsRead: () => set({ unreadAlerts: 0 }),
}));
